/* CAMPO CON DATO VIVO ACCANTO.
 *
 * Il saldo reale sta accanto al campo che lo consuma, non in quattro riquadri
 * in fondo al modulo. Cinque stati, e in quattro di questi l'azione non è
 * premibile — con la RAGIONE scritta lì accanto, non in un messaggio altrove.
 *
 * Il motivo per cui il pulsante è spento e il pulsante devono stare sulla
 * stessa riga: se la ragione è in cima al modulo e il pulsante in fondo, chi
 * prova a premere non sa perché non succede nulla.
 */

import type { ReactNode } from 'react';
import { Pulsante, IconaAvvia } from './Base';
import { Rotella } from './Stati';

export type StatoDatoVivo =
  | 'caricamento'
  | 'letto'
  /** Il dato c'è ma non è di adesso: grigio, con l'ora della lettura. */
  | 'stantio'
  /** Il nodo non risponde: non si può verificare prima di impegnare denaro. */
  | 'muto'
  /** Il capitale basta ma la catena no: gas insufficiente. */
  | 'insufficiente';

export interface PropsDatoVivo {
  /** Il valore letto, già formattato. */
  valore: string;
  /** Riga sotto il valore: cosa è, e quando è stato letto. */
  meta: string;
  stato: StatoDatoVivo;
}

const PUNTO: Record<StatoDatoVivo, string> = {
  caricamento: 'var(--sb-accento-chiaro)',
  letto: 'var(--sb-guadagno)',
  stantio: 'var(--sb-cautela)',
  muto: 'var(--sb-perdita)',
  insufficiente: 'var(--sb-perdita)',
};

const COLORE_VALORE: Record<StatoDatoVivo, string> = {
  caricamento: 'var(--sb-testo-tenue)',
  letto: 'var(--sb-testo)',
  /* Grigio, non ambra: il numero non è sbagliato, è vecchio. */
  stantio: 'var(--sb-testo-spento)',
  muto: 'var(--sb-perdita)',
  insufficiente: 'var(--sb-perdita)',
};

const CLASSE_META: Record<StatoDatoVivo, string> = {
  caricamento: '',
  letto: '',
  stantio: ' sb-dato-vivo__meta--cautela',
  muto: ' sb-dato-vivo__meta--grave',
  insufficiente: ' sb-dato-vivo__meta--grave',
};

export function DatoVivo({ valore, meta, stato }: PropsDatoVivo) {
  return (
    <div className={`sb-dato-vivo sb-dato-vivo--${stato}`}>
      <div className="sb-dato-vivo__riga">
        {stato === 'caricamento' ? (
          <Rotella dim={11} />
        ) : (
          <span
            className={`sb-dato-vivo__punto${stato === 'stantio' ? ' sb-dato-vivo__punto--pulsa' : ''}`}
            style={{ background: PUNTO[stato] }}
            aria-hidden="true"
          />
        )}
        <span className="sb-dato-vivo__valore" style={{ color: COLORE_VALORE[stato] }}>
          {valore}
        </span>
      </div>
      <div className={`sb-dato-vivo__meta${CLASSE_META[stato]}`}>{meta}</div>
    </div>
  );
}

export interface PropsCampoDatoVivo {
  etichetta: string;
  /** Il campo che consuma il dato: quasi sempre un `Campo` numerico. */
  campo: ReactNode;
  vivo: PropsDatoVivo;
  azione: {
    etichetta: string;
    onClick: () => void;
    /** Perché l'azione è bloccata. Se valorizzato il pulsante è spento. */
    motivoBlocco?: string | null;
  };
}

export function CampoDatoVivo({ etichetta, campo, vivo, azione }: PropsCampoDatoVivo) {
  const bloccato = Boolean(azione.motivoBlocco) || vivo.stato === 'caricamento';
  const grave = vivo.stato === 'muto' || vivo.stato === 'insufficiente';

  return (
    <div className="sb-scheda sb-scheda--fitta">
      <div className="sb-campo__nome" style={{ marginBottom: 'var(--sb-s-4)' }}>
        {etichetta}
      </div>

      <div className="sb-riga" style={{ alignItems: 'stretch', gap: 'var(--sb-s-4)' }}>
        <div style={{ flex: '1 1 130px', minWidth: 120 }}>{campo}</div>
        <DatoVivo {...vivo} />
      </div>

      <div className="sb-riga" style={{ marginTop: 'var(--sb-s-6)' }}>
        <Pulsante
          variante="primario"
          onClick={azione.onClick}
          disabled={bloccato}
          style={vivo.stato === 'caricamento' ? { cursor: 'wait' } : undefined}
        >
          <IconaAvvia />
          {azione.etichetta}
        </Pulsante>

        {azione.motivoBlocco && (
          <span
            className="sb-motivo-blocco"
            style={{ color: grave ? '#c98b90' : 'var(--sb-cautela-testo)' }}
          >
            {azione.motivoBlocco}
          </span>
        )}
      </div>
    </div>
  );
}
